import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Dropdown, Input, Modal, Segmented, Select, Table, Tooltip, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import {
  IconAlertTriangle, IconCircleCheck, IconDownload, IconExternalLink, IconFilterOff, IconMapPin,
  IconRefresh, IconSearch, IconTractor, IconUsers,
} from '@tabler/icons-react';
import { useParams, useSearchParams } from 'react-router-dom';
import useAllocationOverview from './useAllocationOverview';
import {
  DEFAULT_VIEW, capacityLabel, capacityPercent, capacityState, countTiles, isAllocated, isAvailableStaff, isGeneric,
  needsPlow, needsStaff, overviewCounts, overviewRows, readView, sectionGroups, setupStatusLabel, viewParams,
} from './overviewModel';
import { isSiteUnavailableInSetup } from '../siteTableUtils';
import styles from './AllocationOverview.module.css';

const GROUP_OPTIONS = [
  { value: 'loader', label: <span className={styles.segment}><IconTractor size={15} />By loader</span> },
  { value: 'site', label: <span className={styles.segment}><IconMapPin size={15} />By site</span> },
  { value: 'staff', label: <span className={styles.segment}><IconUsers size={15} />By staff</span> },
];
const STATUS_TONE = { Confirmed: 'ok', Unconfirmed: 'warn', Estimate: 'muted' };
const CAPACITY_TONE = { under: 'warn', over: 'danger', balanced: 'ok', unknown: 'muted' };
const hasValue = value => value && !['n/a', 'unknown'].includes(String(value).toLowerCase());

const Pill = ({ tone = 'muted', children }) => <span className={`${styles.pill} ${styles[`tone_${tone}`]}`}>{children}</span>;
const Muted = ({ children = '—' }) => <span className={styles.muted}>{children}</span>;

function LoaderCell({ row }) {
  if (!row.vehicles.length) return row.sites.length ? <Pill tone="warn">No loader</Pill> : <Muted />;
  return row.vehicles.map(v => (
    <div key={v.vehicle_id} className={styles.entity}>
      <div className={styles.primary}><IconTractor size={14} className={styles.icon} />{v.vehicle_name || `Loader ${v.vehicle_id}`}</div>
      <div className={styles.tags}>
        {v.vehicle_subtype && <span className={styles.chip}>{v.vehicle_subtype}</span>}
        {isGeneric(v) && <Pill tone="gold">Generic</Pill>}
        {v.sites.length > 1 && <Pill tone="info">Shared · {v.sites.length} sites</Pill>}
        {!isAllocated(v) && <Pill>Unallocated</Pill>}
        {v.archived && <Pill>Unavailable</Pill>}
      </div>
    </div>
  ));
}

function CapacityBar({ site }) {
  const state = capacityState(site);
  const percent = capacityPercent(site);
  if (state === 'unknown') return <div className={styles.capacityText}><Muted>Capacity not specified</Muted></div>;
  return (
    <Tooltip title={capacityLabel(site)}>
      <div className={styles.capacity}>
        <div className={styles.bar}><span className={styles[`fill_${CAPACITY_TONE[state]}`]} style={{ width: `${Math.min(percent, 100)}%` }} /></div>
        <span className={`${styles.capacityText} ${styles[`text_${CAPACITY_TONE[state]}`]}`}>
          {percent}% · {Number(site.parking_lots_sqft_allocated || 0).toLocaleString()} / {Number(site.parking_lots_sqft).toLocaleString()} sq ft
        </span>
      </div>
    </Tooltip>
  );
}

function SitesCell({ row }) {
  if (!row.sites.length) return <Muted>No site</Muted>;
  return row.sites.map(s => (
    <div key={s.site_id} className={styles.entity}>
      <div className={styles.primary}>
        <span>{s.site_name || `Site ${s.site_id}`}</span>
        <Pill tone={STATUS_TONE[s.contract_status] || 'muted'}>{s.contract_status || 'Unknown'}</Pill>
        {isSiteUnavailableInSetup(s) && <Pill>Unavailable</Pill>}
      </div>
      <div className={styles.secondary}>{[s.client_name || 'No customer', s.region || 'No region'].join(' · ')}</div>
      <CapacityBar site={s} />
    </div>
  ));
}

function PlowCell({ row }) {
  if (!row.vehicles.length) return <Muted />;
  return row.vehicles.map(v => {
    const plow = v.assigned_plow;
    if (!plow) return <div key={v.vehicle_id} className={styles.entity}>{needsPlow(v) ? <Pill tone="warn">No plow</Pill> : <Muted>No plow</Muted>}</div>;
    const detail = [...new Set([plow.plow_model, plow.plow_size].filter(hasValue))].join(' · ');
    return (
      <div key={v.vehicle_id} className={styles.entity}>
        <div className={styles.primary}>{plow.plow_name || `Plow ${plow.plow_id}`}
          {plow.is_confirmed === true ? <Pill tone="ok">Confirmed</Pill> : plow.is_confirmed === false ? <Pill tone="warn">Unconfirmed</Pill> : <Pill>Unknown</Pill>}
          {plow.archived && <Pill>Unavailable</Pill>}
        </div>
        {detail && <div className={styles.secondary}>{detail}</div>}
      </div>
    );
  });
}

function DoubleDownCell({ row }) {
  if (!row.vehicles.length) return <Muted />;
  return row.vehicles.map(v => {
    const dd = v.assigned_doubleDown;
    if (!dd) return <div key={v.vehicle_id} className={styles.entity}><Muted /></div>;
    const detail = [dd.doubleDown_model, dd.doubleDown_type_name].filter(hasValue).join(' · ');
    return (
      <div key={v.vehicle_id} className={styles.entity}>
        <div className={styles.primary}>{dd.doubleDown_name || `DoubleDown ${dd.doubleDown_id}`}{dd.archived && <Pill>Unavailable</Pill>}</div>
        {detail && <div className={styles.secondary}>{detail}</div>}
      </div>
    );
  });
}

const initials = name => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();

function StaffCell({ row }) {
  if (!row.people.length) return row.vehicles.some(needsStaff) ? <Pill tone="warn">No staff</Pill> : <Muted />;
  const standalone = !row.vehicles.length;
  return row.people.map(person => (
    <div key={person.staff_id} className={styles.person}>
      <span className={styles.avatar} aria-hidden="true">{initials(person.name)}</span>
      <span>
        <span className={styles.primary}>{person.name || `Staff ${person.staff_id}`}{person.archived && <Pill>Unavailable</Pill>}</span>
        <span className={styles.secondary}>{person.position || 'No position'}</span>
        {standalone && (isAvailableStaff({ ...person, loaders: person.loaders || [] })
          ? <Pill tone="info">Available</Pill> : <Pill>Other role</Pill>)}
      </span>
    </div>
  ));
}

const sectionSpan = (span) => r => (r.kind === 'section' ? { colSpan: span } : {});
export const OVERVIEW_COLUMNS = [
  { key: 'loader', title: 'Loader', width: 240, onCell: sectionSpan(5),
    render: (_, r) => (r.kind === 'section'
      ? <div className={styles.sectionLabel}>{r.section}<span className={styles.sectionCount}>{r.count}</span></div>
      : <LoaderCell row={r} />) },
  { key: 'sites', title: 'Sites & capacity', width: 380, onCell: sectionSpan(0), render: (_, r) => r.kind !== 'section' && <SitesCell row={r} /> },
  { key: 'plow', title: 'Plow', width: 230, onCell: sectionSpan(0), render: (_, r) => r.kind !== 'section' && <PlowCell row={r} /> },
  { key: 'doubleDown', title: 'DoubleDown', width: 200, onCell: sectionSpan(0), render: (_, r) => r.kind !== 'section' && <DoubleDownCell row={r} /> },
  { key: 'staff', title: 'Staff', width: 260, onCell: sectionSpan(0), render: (_, r) => r.kind !== 'section' && <StaffCell row={r} /> },
];
const TABLE_WIDTH = OVERVIEW_COLUMNS.reduce((sum, column) => sum + column.width, 0);

export function openOverviewTab(setupId, view = DEFAULT_VIEW) {
  window.open(`/allocate/overview/${encodeURIComponent(setupId)}?${viewParams(view)}`, '_blank', 'noopener,noreferrer');
}

// Only the "setup is being edited" case has a message worth showing; network/server details are not.
const downloadErrorMessage = error => (error?.name === 'SetupChangingError'
  ? 'The setup changed while the PDF was being prepared. Please try again in a moment.'
  : 'Overview download failed. Please retry.');

async function download(setupId, group) {
  // PDF dependencies are loaded only when an export is requested.
  const { downloadOverviewPdf } = await import('./overviewPdf');
  await downloadOverviewPdf(setupId, group);
}

function useViewportHeight() {
  const [height, setHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const update = () => setHeight(window.innerHeight);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return height;
}

function CountTile({ tile, active, onClick }) {
  const Icon = tile.tone === 'warn' || tile.tone === 'danger' ? IconAlertTriangle : tile.tone === 'ok' ? IconCircleCheck : null;
  return (
    <button type="button" aria-pressed={active} aria-label={`${tile.label}: ${tile.value}`} onClick={onClick}
      className={`${styles.tile} ${styles[`tile_${tile.tone}`]} ${active ? styles.tileActive : ''}`}>
      <span className={styles.tileLabel}>{Icon && <Icon size={14} />}{tile.label}</span>
      <span className={styles.tileValue}>{tile.value}</span>
      {tile.progress !== undefined
        ? <span className={styles.tileProgress}><span style={{ width: `${Math.round(tile.progress * 100)}%` }} /></span>
        : <span className={styles.tileDetail}>{tile.tone === 'ok' ? 'All clear' : tile.detail}</span>}
    </button>
  );
}

export function AllocationOverview({ setupId, view, onViewChange, standalone = false }) {
  const { data, setup, updatedAt, loading, unavailable, error, refresh, locked } = useAllocationOverview(setupId);
  const [exporting, setExporting] = useState(false);
  const viewportHeight = useViewportHeight();
  const rows = useMemo(() => data ? overviewRows(data, view) : [], [data, view]);
  const tableRows = useMemo(() => sectionGroups(rows).flatMap(({ section, rows: members }, index) => [
    { key: `section:${index}:${section}`, kind: 'section', section, count: members.length }, ...members,
  ]), [rows]);
  const tiles = useMemo(() => data ? countTiles(overviewCounts(data, view.statuses)) : [], [data, view.statuses]);
  const change = patch => onViewChange({ ...view, ...patch });
  const isFiltered = viewParams({ ...view, group: DEFAULT_VIEW.group }) !== viewParams(DEFAULT_VIEW);
  const exportPdf = async () => {
    setExporting(true);
    try { await download(setupId, view.group); } catch (e) { message.error(downloadErrorMessage(e)); }
    finally { setExporting(false); }
  };
  const regions = data ? [...new Set(data.sites.map(s => s.region).filter(Boolean))].sort() : [];
  const statuses = [...new Set(['Confirmed', 'Unconfirmed', 'Estimate', ...(data?.sites.map(s => s.contract_status).filter(Boolean) || [])])];
  const statusLabel = setupStatusLabel(setup);
  const liveTone = error ? 'warn' : loading ? 'busy' : 'ok';
  const tableHeight = Math.max(320, viewportHeight - (standalone ? 400 : 470));

  return (
    <section className={styles.overview} aria-label="Allocation overview">
      <header className={`${styles.hero} ${standalone ? '' : styles.heroInModal}`}>
        <div className={styles.heroText}>
          <span className={styles.eyebrow}>Allocation overview</span>
          <h2 className={styles.title}>{setup?.name || 'Loading setup…'}</h2>
          <div className={styles.meta}>
            <span className={`${styles.status} ${styles[`status_${statusLabel.toLowerCase()}`]}`}>{statusLabel}</span>
            <span className={styles.live} role="status">
              <span className={`${styles.liveDot} ${styles[`live_${liveTone}`]}`} />
              {loading ? 'Refreshing…' : updatedAt ? `Live · updated ${updatedAt.toLocaleTimeString()}` : 'Not yet loaded'}
            </span>
            <span className={styles.metaNote}>{locked ? 'Locked setup · checks every 2 min and after local changes' : 'Auto-refreshes every 30 s while visible'}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <Tooltip title="Refresh now"><Button aria-label="Refresh" icon={<IconRefresh size={16} />} onClick={refresh} loading={loading} /></Tooltip>
          {!standalone && <Button icon={<IconExternalLink size={16} />} onClick={() => openOverviewTab(setupId, view)}>Open in new tab</Button>}
          <Tooltip title="Downloads the entire setup, all site statuses, regardless of filters">
            <Button type="primary" className={styles.download} icon={<IconDownload size={16} />} onClick={exportPdf} loading={exporting} disabled={!data || unavailable}>Download PDF</Button>
          </Tooltip>
        </div>
      </header>

      {error && <Alert className={styles.alert} type={unavailable ? 'error' : 'warning'} showIcon message={error} action={<Button size="small" onClick={refresh}>Retry</Button>} />}

      {!unavailable && <>
        {tiles.length > 0 && <div className={styles.tiles}>
          {tiles.map(tile => <CountTile key={tile.id} tile={tile} active={view.gap === tile.id} onClick={() => change({ gap: view.gap === tile.id ? '' : tile.id })} />)}
        </div>}

        <div className={styles.toolbar}>
          <Segmented aria-label="Group by" value={view.group} onChange={group => change({ group })} options={GROUP_OPTIONS} />
          <Input aria-label="Search overview" placeholder="Search loaders, sites, staff, plows…" allowClear value={view.search}
            prefix={<IconSearch size={16} className={styles.muted} />} onChange={e => change({ search: e.target.value })} className={styles.search} />
          <Select aria-label="Region" value={view.region} onChange={region => change({ region })} className={styles.select}
            options={[{ value: 'all', label: 'All regions' }, { value: 'none', label: 'No site/region' }, ...regions.map(value => ({ value, label: value }))]} />
          <Select aria-label="Site contract status" mode="multiple" maxTagCount="responsive" value={view.statuses} onChange={next => change({ statuses: next })}
            options={statuses.map(value => ({ value, label: value }))} placeholder="Site statuses" className={styles.statuses} />
          <Select aria-label="Loader allocation status" value={view.allocation} onChange={allocation => change({ allocation })} className={styles.select}
            options={[{ value: 'all', label: 'All loaders' }, { value: 'allocated', label: 'Allocated loaders' }, { value: 'unallocated', label: 'Unallocated loaders' }]} />
          {isFiltered && <Button type="link" icon={<IconFilterOff size={15} />} onClick={() => onViewChange({ ...DEFAULT_VIEW, group: view.group })}>Clear filters</Button>}
        </div>

        <div className={styles.resultLine}>
          <strong>{rows.length} matching {rows.length === 1 ? 'row' : 'rows'}</strong>
          <span>Loader and staff counts cover the entire setup · capacity counts cover {view.statuses.join(', ') || 'no selected statuses'}</span>
        </div>

        <Table className={styles.grid} virtual rowKey="key" size="small" pagination={false} columns={OVERVIEW_COLUMNS} dataSource={tableRows}
          rowClassName={r => (r.kind === 'section' ? styles.sectionRow : styles.dataRow)}
          loading={loading && !data} scroll={{ x: TABLE_WIDTH, y: tableHeight }}
          locale={{ emptyText: data ? 'No allocations match this view.' : 'Loading allocation overview…' }} />
      </>}
    </section>
  );
}

export function OverviewMenu({ setupId }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState({ ...DEFAULT_VIEW });
  const action = async ({ key }) => {
    if (key === 'popup') setOpen(true);
    else if (key === 'tab') openOverviewTab(setupId);
    else {
      setExporting(true);
      try { await download(setupId, 'loader'); } catch (e) { message.error(downloadErrorMessage(e)); }
      finally { setExporting(false); }
    }
  };
  return <>
    <Dropdown trigger={['click']} disabled={!setupId || exporting} menu={{ onClick: action, items: [
      { key: 'popup', label: 'Overview pop up' }, { key: 'tab', label: 'Overview in a new tab' }, { key: 'download', label: 'Overview download' },
    ] }}><Button disabled={!setupId} loading={exporting}>Overview <DownOutlined /></Button></Dropdown>
    <Modal open={open} onCancel={() => setOpen(false)} footer={null} width="96vw" style={{ top: 20 }} destroyOnClose
      className={styles.modal} aria-label="Allocation overview">
      {open && setupId && <AllocationOverview setupId={setupId} view={view} onViewChange={setView} />}
    </Modal>
  </>;
}

export default function AllocationOverviewPage() {
  const { setupId } = useParams();
  const [params, setParams] = useSearchParams();
  const view = useMemo(() => readView(params), [params]);
  if (!/^\d+$/.test(setupId || '')) return <Alert type="error" message="Invalid allocation setup." />;
  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <AllocationOverview setupId={setupId} view={view} onViewChange={next => setParams(viewParams(next), { replace: true })} standalone />
      </div>
    </main>
  );
}
